import {
  ActivityPlayerApi,
  ActivityStatus,
  ActivityType,
  ActivityViewMode,
  type ButtonProps,
  toast,
} from "@cca/components";
import { ActivityDto } from "@core/typesV2";
import { useIsActivitySubmittable } from "@providersV2";
import { UseMutationResult, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { SubmitActivityMutationParam } from "../providers";
import { isActivityWithProgress } from "../utils";

type UseNavigationButtonProps = {
  userIsLearner: boolean;
  courseId: string;
  dayIds: string[];
  dayIndex: number;
  activities: ActivityDto[];
  currActivityIndex: number;
  currPageIsLast: boolean;
  nextDayLink: string;
  viewMode: ActivityViewMode;
  activityPlayerRef: React.RefObject<ActivityPlayerApi>;
  onNextPageButtonClick: () => void;
  submitActivityMutation?: UseMutationResult<
    void,
    Error,
    SubmitActivityMutationParam,
    unknown
  >;
  openCompleteDialog?: (confirmFn: () => void) => void;
  flushProgress?: () => Promise<void> | void;
};

export type NavigationState = {
  openFirstLesson?: boolean;
  showCongratulations?: boolean;
};

export type NavigationButtonProps = ButtonProps<"button">;

export const useNavigationButton = ({
  userIsLearner,
  courseId,
  dayIds,
  dayIndex,
  activities,
  currActivityIndex,
  currPageIsLast,
  nextDayLink,
  viewMode,
  activityPlayerRef,
  submitActivityMutation,
  onNextPageButtonClick,
  openCompleteDialog,
  flushProgress,
}: UseNavigationButtonProps) => {
  const { tab } = useParams<{
    tab: string;
  }>();

  const currActivity = activities.at(currActivityIndex);

  const currActivityIsSubmitted =
    viewMode === "edit"
      ? currActivity
        ? isActivityWithProgress(currActivity) &&
          (currActivity.progress?.status === ActivityStatus.Completed ||
            currActivity.progress?.status === ActivityStatus.Modified)
        : false
      : true;

  const dayNotCompletedActivity = activities.find(
    (activity, index) =>
      isActivityWithProgress(activity) &&
      activity?.progress?.status === ActivityStatus.InProgress &&
      index !== currActivityIndex,
  );

  const baseUrl = `/courses-v2/${tab}/${courseId}`;
  const nextActivity =
    dayNotCompletedActivity ??
    activities[currActivityIndex + 1] ??
    activities[0]?.activityId;
  const nextActivityId = nextActivity?.activityId;
  const nextActivityLink = nextActivityId
    ? `../activities/${nextActivityId}`
    : undefined;

  const activityIsLastInDay = currActivityIndex === activities.length - 1;
  const dayIsLastInCourse = dayIndex === dayIds.length - 1;

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const activityId = currActivity?.activityId;
  const contentQueryEnabled =
    !userIsLearner ||
    (!!currActivity &&
      isActivityWithProgress(currActivity) &&
      currActivity.progress?.status !== ActivityStatus.NotStarted);

  const activityIsSubmittable = useIsActivitySubmittable({
    courseId,
    activityId,
    attempt: currActivity?.progress?.attempt,
    enabled: contentQueryEnabled,
  });

  const submitCurrActivity = useCallback(
    async (redirectLink: string) => {
      if (
        !submitActivityMutation ||
        submitActivityMutation.isPending ||
        !activityId
      ) {
        return console.error("Cannot submit the current activity");
      }

      await flushProgress?.();

      submitActivityMutation.mutate(
        { activityId },
        {
          onSuccess: () => {
            if (activityIsSubmittable) {
              toast.success({ title: "Activity submitted successfully" });
            }

            if (currActivity.type === ActivityType.Assessment) {
              // Invalidate assets after submit assesment to see auto grade result
              queryClient.invalidateQueries({
                queryKey: ["assets", "progress", { courseId, activityId }],
                exact: false,
              });
            }

            navigate(redirectLink, {
              state: {
                showCongratulations:
                  currActivityIndex === activities.length - 1,
              } satisfies NavigationState,
            });

            queryClient.invalidateQueries({
              queryKey: ["courses", courseId, undefined],
              exact: true,
            });
          },
        },
      );
    },
    [
      submitActivityMutation,
      activityId,
      flushProgress,
      activityIsSubmittable,
      currActivity?.type,
      navigate,
      currActivityIndex,
      activities.length,
      queryClient,
      courseId,
    ],
  );

  const getSubmitButtonProps = useCallback(
    (redirectLink: string) =>
      ({
        children: "Submit",
        onClick: () => {
          if (activityPlayerRef.current?.validateAll() === true) {
            if (openCompleteDialog) {
              openCompleteDialog(() => submitCurrActivity(redirectLink));
            } else {
              submitCurrActivity(redirectLink);
            }
          }
        },
        loading: submitActivityMutation?.isPending,
      }) satisfies NavigationButtonProps,
    [
      submitActivityMutation?.isPending,
      activityPlayerRef,
      openCompleteDialog,
      submitCurrActivity,
    ],
  );

  const getNextButtonProps = (text: string, redirectLink: string) =>
    ({
      children: text,
      onClick: () => {
        if (currActivityIsSubmitted) {
          navigate(redirectLink);
        } else {
          submitCurrActivity(redirectLink);
        }
      },
    }) satisfies NavigationButtonProps;

  if (!currPageIsLast) {
    return {
      children: "Next",
      onClick: onNextPageButtonClick,
    } satisfies NavigationButtonProps;
  }

  if (activityIsSubmittable === undefined)
    return {
      children: "Next...",
      disabled: true,
      onClick: () => {},
    } satisfies NavigationButtonProps;

  const shouldRenderSubmitButton =
    activityIsSubmittable && !currActivityIsSubmitted;

  if (activityIsLastInDay && nextActivityLink) {
    return shouldRenderSubmitButton
      ? getSubmitButtonProps(nextActivityLink)
      : getNextButtonProps(`Next ${nextActivity.type}`, nextActivityLink);
  }

  if (!activityIsLastInDay && nextActivityLink) {
    return shouldRenderSubmitButton
      ? getSubmitButtonProps(nextActivityLink)
      : getNextButtonProps(`Next ${nextActivity.type}`, nextActivityLink);
  }

  if (!dayIsLastInCourse) {
    return shouldRenderSubmitButton
      ? getSubmitButtonProps(nextDayLink)
      : getNextButtonProps("Next Day", nextDayLink);
  }

  if (!dayIsLastInCourse && !currActivityIsSubmitted) {
    return getSubmitButtonProps(baseUrl);
  }

  return shouldRenderSubmitButton
    ? getSubmitButtonProps(baseUrl)
    : {
        children:
          userIsLearner && !currActivityIsSubmitted
            ? "Complete Course"
            : "Close",
        onClick: () => {
          if (userIsLearner && !currActivityIsSubmitted) {
            submitCurrActivity(baseUrl);
          } else {
            navigate(baseUrl);
          }
        },
      };
};
